import { Box, Typography, Link } from '@mui/material';
import ReactMarkdown from 'markdown-to-jsx';
import React from 'react';
import { NavLink } from 'react-router-dom';

/**
 * Markdown Component
 *
 * This component is used to render Markdown content with custom styling and behavior.
 * It leverages `ReactMarkdown` to parse and render Markdown syntax, while allowing
 * developers to override specific elements (e.g., headings, paragraphs, links, list items)
 * with Material-UI components for consistent styling.
 *
 * Props:
 * @param {React.ReactNode} children - The Markdown content to be rendered.
 *
 * Features:
 * - Customizes Markdown elements (e.g., `h1`, `h2`, `p`, `a`, `li`) with Material-UI components
 *   such as `Typography`, `Box`, and `Link`.
 * - Automatically detects external links and opens them in a new tab, while internal links
 *   are rendered using `NavLink` for navigation within the application.
 * - Provides a consistent and styled rendering of Markdown content, making it suitable
 *   for use in blogs, documentation, or any application requiring Markdown support.
 *
 * Usage:
 * ```jsx
 * <Markdown>
 *   {`# Heading 1
 *   ## Heading 2
 *   This is a [link](https://example.com).`}
 * </Markdown>
 * ```
 */
const Markdown = ({ children }) => {
  function MarkdownListItem(props) {
    return (
      <Box component="li" sx={{ mt: 1, typography: 'body1' }} {...props} />
    );
  }

  const markdownOptions = {
    overrides: {
      h1: {
        component: Typography,
        props: {
          gutterBottom: true,
          variant: 'h4',
          component: 'h1',
        },
      },
      h2: {
        component: Typography,
        props: { gutterBottom: true, variant: 'h6', component: 'h2' },
      },
      h3: {
        component: Typography,
        props: { gutterBottom: true, variant: 'subtitle1' },
      },
      h4: {
        component: Typography,
        props: {
          gutterBottom: true,
          variant: 'caption',
          paragraph: true,
        },
      },
      p: {
        component: Typography,
        props: { paragraph: true },
      },
      a: {
        component: ({ href, ...props }) => {
          const isExternal =
            href && (href.startsWith('http://') || href.startsWith('https://'));
          if (isExternal) {
            return <Link href={href} target="_blank" {...props} />;
          } else {
            return <Link component={NavLink} to={href} {...props} />;
          }
        },
      },
      li: {
        component: MarkdownListItem,
      },
    },
  };

  return <ReactMarkdown options={markdownOptions}>{children}</ReactMarkdown>;
};

export default Markdown;
